// src/context/ThemeContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * ThemeContext: tarjoaa teeman ("light" | "dark") ja vaihtofunktion.
 * - Tallennetaan teema localStorageen ('theme')
 * - Synkataan <html>-elementin 'dark' -luokka Tailwindia varten
 * - Synkataan CSS-muuttujat (--bg-start, --bg-end) gradienttia varten
 * - Oletus: OS:n prefers-color-scheme, jos localStoragessa ei arvoa
 */

// 1) Luodaan itse konteksti
const ThemeContext = createContext(null);

// 2) Pieni apu: luetaan alkuteema turvallisesti (ei riko SSR:ää)
function getInitialTheme() {
  try {
    const saved = localStorage.getItem("theme");
    if (saved === "dark" || saved === "light") return saved;
  } catch {}
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

// 3) Provider-komponentti: kietoo sovelluksen ja tarjoaa arvot lapsille
export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  // Synkkaa <html>-elementin luokka, localStorage ja CSS-muuttujat
  useEffect(() => {
    const root = document.documentElement;

    if (theme === "dark") {
      root.classList.add("dark");
      root.style.setProperty("--bg-start", "#0A0014"); // yläväri (dark)
      root.style.setProperty("--bg-end", "#2A006B");   // alaväri (dark)
    } else {
      root.classList.remove("dark");
      root.style.setProperty("--bg-start", "#99925c"); // yläväri (light)
      root.style.setProperty("--bg-end", "#fff455");   // alaväri (light)
    }

    try {
      localStorage.setItem("theme", theme);
    } catch {}
  }, [theme]);

  // Vaihtokytkin: light <-> dark
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // Memo: ei luoda uutta objektia joka renderissä
  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// 4) Mukava hook-käyttöliittymä
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme pitää kutsua ThemeProviderin sisällä.");
  }
  return ctx;
}
